import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { createPost, friendlyCallError, displayHandle } from '../../utils/reviews';
import { StarInput } from './bits';

const MAX = 500;
const GUEST_POSTS_KEY = 'volcyy:guestPostCount';

// A guest is nudged to claim their account after their 1st and 3rd post —
// enough to be seen, not so often it gets in the way of posting.
const CLAIM_PROMPT_AT = [1, 3];

function bumpGuestPostCount(uid) {
  try {
    const n = (Number(localStorage.getItem(`${GUEST_POSTS_KEY}:${uid}`)) || 0) + 1;
    localStorage.setItem(`${GUEST_POSTS_KEY}:${uid}`, String(n));
    return n;
  } catch {
    return 0;
  }
}

// One box for everything: write something, optionally add stars. With stars
// it is a review and counts toward the rating; without, it's a comment. No
// sign-up needed — the first post quietly makes a guest account.
// DEMO TOOLS: `asDemoUser` (admin only) sends the post as a fresh generated user.
export default function Composer({ restaurant, onPosted, asDemoUser }) {
  const {
    user, userProfile, isGuest, guestProfile, isAdmin,
    ensureGuest, refreshGuestProfile, openClaim,
  } = useAuth();
  const [text, setText] = useState('');
  const [stars, setStars] = useState(null);
  const [asPrompt, setAsPrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Folded away until someone wants it. An empty three-row box at the top
  // of every restaurant page was the single biggest thing between a reader
  // and the comments they came for.
  const [open, setOpen] = useState(false);
  const textRef = useRef(null);

  const isOwner = !!user && restaurant.ownerId === user.uid;
  // Owners were able to pin prompts on their own listing; turned off at the
  // client's request. Add `isOwner ||` back here and in createPost to restore it.
  const canPrompt = isAdmin || userProfile?.isStaff === true;
  const remaining = MAX - text.length;

  let identity;
  if (asDemoUser) identity = <>Posting as <strong>a generated user</strong> · demo</>;
  else if (isOwner) identity = <>Posting as <strong>{restaurant.name}</strong> · Owner</>;
  else if (user) identity = <>Posting as <strong>{displayHandle(userProfile) || 'you'}</strong></>;
  else if (isGuest && guestProfile) {
    identity = (
      <>Posting as <strong>{guestProfile.handle}</strong> · <button type="button" className="composer__link" onClick={openClaim}>Claim your account</button></>
    );
  } else identity = <>No sign-up needed. You'll post as a guest.</>;

  useEffect(() => {
    if (open) textRef.current?.focus();
  }, [open]);

  async function submit(e) {
    e.preventDefault();
    if (busy || text.trim().length < 2) return;
    setBusy(true);
    setError('');
    const postingAsGuest = !user;
    try {
      const postingUser = await ensureGuest();
      const res = await createPost({
        businessId: restaurant.id,
        text,
        rating: asPrompt ? null : stars,
        asPrompt: canPrompt && asPrompt && !asDemoUser,
        asDemoUser: !!asDemoUser,
      });
      const post = res.post;
      setText('');
      setStars(null);
      setAsPrompt(false);
      setOpen(false);
      onPosted?.(post || { id: res.id, status: res.status }, res);
      if (postingAsGuest) {
        await refreshGuestProfile();
        if (CLAIM_PROMPT_AT.includes(bumpGuestPostCount(postingUser.uid))) openClaim();
      }
    } catch (err) {
      setError(friendlyCallError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="composer__open" onClick={() => setOpen(true)}>
        <span className="composer__open-stars" aria-hidden="true">★</span>
        {isOwner ? 'Reply as the venue' : 'Write a review'}
      </button>
    );
  }

  return (
    <form className="composer" onSubmit={submit}>
      <label htmlFor="composer-text" className="composer__sr">Your review or comment</label>
      <textarea
        ref={textRef}
        id="composer-text"
        className="composer__text"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX))}
        placeholder={asPrompt ? 'Ask the crowd something… e.g. "Wildest thing you saw here? Go."' : "What did you really think? Say it with your chest."}
        rows={3}
        maxLength={MAX}
        disabled={busy}
      />
      <div className="composer__row">
        {(!isOwner || asDemoUser) && !asPrompt && (
          <div className="composer__stars">
            <span className="composer__stars-label">{stars ? `${stars}/5` : 'Rate it'}</span>
            <StarInput value={stars} onChange={setStars} disabled={busy} />
          </div>
        )}
        {canPrompt && !asDemoUser && (
          <label className="composer__prompt">
            <input type="checkbox" checked={asPrompt} onChange={(e) => setAsPrompt(e.target.checked)} disabled={busy} />
            Pinned prompt
          </label>
        )}
        <span className={`composer__count${remaining < 40 ? ' composer__count--low' : ''}`} aria-live="polite">
          {remaining < 100 ? remaining : ''}
        </span>
        <button type="button" className="composer__cancel" onClick={() => { setOpen(false); setError(''); }} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="composer__submit" disabled={busy || text.trim().length < 2}>
          {busy ? 'Posting…' : 'Post'}
        </button>
      </div>
      {error && <p className="composer__error" role="alert">{error}</p>}
      <p className="composer__meta">
        {identity}
        <span className="composer__terms"> · 18+ · <Link to="/terms">Rules</Link></span>
      </p>
    </form>
  );
}
